import React, { useMemo, useState, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import styles from 'assets/jss/material-dashboard-pro-react/views/prepaidLoyalty/numbersTabStyle';
import { translate } from 'components/Translation';
import Table from 'components/CustomTable/CustomTable';
import TableContainer from '@material-ui/core/TableContainer';
import TableHeader from 'components/TableHeader/TableHeader';
import { PhoneNumberCell, CheckboxCell, DepartmentsCell, UserNameCell, LinkCell } from 'components/Cells/Cells';
import { primaryColor } from 'assets/jss/material-dashboard-pro-react';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { VouchersService } from 'services/vouchers/vouchers';
import { BulkNumber, SingleNumber } from 'views/BulkPurchaseDevices/BulkPurchase';
import { dispatch } from 'redux/store';
import { setToast } from 'redux/modules/toast/actions';
import { getSmartVoucherError } from 'views/BulkPurchaseDevices/common/commons';
import AlternativeSelect from 'components/AlternativeSelect/AlternativeSelect';
import { EshopProductsService } from 'services/eshop/products';
import { finalize } from 'rxjs/operators';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { allAccountSelector, selectedAccountSelector } from 'redux/modules/addServices/selectors';
import { useSelector } from 'react-redux';
// @ts-ignore
const useStyles = makeStyles(styles);

type ISelectNumbersListProps = {
	numbersLoading: boolean;
	numbersData: SingleNumber[];
	changeSelected: (clickedNumber: SingleNumber, changedToSelected: boolean, discount?: number) => void;
	cartNumbers: BulkNumber[];
	setDisableNumber: (number: SingleNumber) => void;
	departments: SingleDepartment[];
	tableLoading: boolean;
	setTableLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

const SelectNumbersList = ({
	numbersLoading,
	numbersData,
	changeSelected,
	cartNumbers,
	setDisableNumber,
	departments,
	tableLoading,
	setTableLoading,
}: ISelectNumbersListProps) => {
	const classes = useStyles();
	const corpAccounts = useSelector(allAccountSelector);
	const [searchInputValue, setSearchInputValue] = useState<string>('');
	const [departmentIdToFilterBy, setDepartmentIdToFilterBy] = useState<string | null>(null);

	const departmentToFilterBy = departments.find((department) => department.deptId === departmentIdToFilterBy);
	const departmentToFilterByName = departmentToFilterBy ? departmentToFilterBy.name : translate('all.departments');

	const data: SingleNumber[] = useMemo(() => {
		return numbersData
			.filter((number) => {
				return departmentIdToFilterBy === null || number.deptId === departmentIdToFilterBy;
			})
			.filter((number) => {
				return number['accountNum'].toLowerCase().includes(searchInputValue.toLowerCase());
			});
	}, [searchInputValue, numbersData, departmentIdToFilterBy]);

	const departmentsMap = useMemo(
		() => departments.reduce((acc, department) => ({ ...acc, [department.deptId]: department }), {}),
		[departments]
	);

	const onNumberSelected = (clickedNumber: SingleNumber, changedToSelected: boolean) => {
		if (changedToSelected) {
			checkIneligibility(clickedNumber);
		} else {
			const matchingCartNumber = cartNumbers.find(
				(cartNumber) => cartNumber.accountNumber === clickedNumber.accountNum
			);
			if (matchingCartNumber && matchingCartNumber.orderItemId) {
				removeSingleCartItem(clickedNumber, matchingCartNumber.orderItemId);
			} else {
				changeSelected(clickedNumber, false);
			}
		}
	};

	const removeSingleCartItem = (clickedNumber: SingleNumber, orderItemId: string) => {
		setTableLoading(true);
		EshopProductsService.removeSingleCartItem(orderItemId)
			.pipe(
				finalize(() => {
					setTableLoading(false);
				})
			)
			.subscribe(
				() => {
					changeSelected(clickedNumber, false);
				},
				() => {
					dispatch(
						setToast({
							error: true,
							label: translate('error.generic-title'),
							actionType: translate('error'),
						})
					);
				}
			);
	};

	const checkIneligibility = useCallback(
		(original: SingleNumber) => {
			setTableLoading(true);
			const getOrganization = corpAccounts.find(({ accountId }: any) => accountId === original.accountId)
			VouchersService.getSmartVoucher(getOrganization ? getOrganization?.documentId.toString() : '', original?.accountNum || '').subscribe(
				({ vouchers }) => {
					const upFrontPaymentCommitments = (vouchers || []).filter(
						(voucher) => !!(voucher?.installments || []).find((installment) => installment?.duration === 0)
					);

					if (upFrontPaymentCommitments?.length) {
						changeSelected(original, true, upFrontPaymentCommitments[0].deviceDiscount);
					} else {
						setDisableNumber(original);
					}
				},
				({ response }) => {
					const error = getSmartVoucherError(response);
					if (error.isEligible) {
						setTableLoading(false);
					} else {
						setDisableNumber(original);
					}
					dispatch(
						setToast({
							error: true,
							label: translate(error.errorDescription),
							actionType: translate('error'),
						})
					);
				}
			);
		},
		[setTableLoading, changeSelected, setDisableNumber]
	);

	return (
		<>
			<div className={classes.departmentSelect}>
				<AlternativeSelect
					value={<div className={classes.departmentSelectValue}>{departmentToFilterByName}</div>}
					items={[
						{ name: translate('all.departments'), id: null },
						...Object.values(departments).map((department) => ({
							name: department.name,
							id: department.deptId,
						})),
					]}
					selectedItems={[]}
					onClick={(item) => setDepartmentIdToFilterBy(item.id)}
				/>
			</div>
			<div className={classes.tab}>
				<div className={classes.searchWrapper}>
					<TableHeader
						label={translate('search')}
						setSearchInputValue={(value) => setSearchInputValue(value)}
						searchInputValue={searchInputValue}
					/>
				</div>
				<TableContainer className={classes.tableContainer}>
					<Table
						total={data.length}
						withHeader
						alignColumnsRight={[4]}
						tableProps={{
							loading: numbersLoading || tableLoading,
							data,
							defaultPageSize: 10,
							columns: [
								{
									Header: translate('table.phone.number'),
									Cell: ({ original }: { original: SingleNumber }) => (
										<PhoneNumberCell text={original.accountNum} />
									),
									accessor: 'accountNum',
								},
								{
									Header: translate('table.user.name'),
									Cell: ({ original }: { original: SingleNumber }) => (
										<UserNameCell text={original.login} />
									),
									accessor: 'LOGIN',
								},
								{
									Header: translate('numbers.department'),
									id: 'department',
									accessor: (row: SingleNumber) =>
										departmentsMap[row.deptId]
											? departmentsMap[row.deptId].name
											: translate('numbers.details.notAssigned'),
									Cell: ({ original }: { original: SingleNumber }) => {
										const { deptId } = original;
										const dept = departmentsMap[deptId];
										return (
											<DepartmentsCell
												departments={
													dept
														? ([
															{
																name: dept.name,
																uiColor: dept.uiColor,
																deptId: dept.deptId,
															},
														] as SingleDepartment[])
														: []
												}
											/>
										);
									},
								},
								{
									Header: translate(
										'purchase.bulk.devices.selected.numbers',
										cartNumbers.length,
										numbersData.length
									),
									id: 'selected',
									accessor: (row: SingleNumber) =>
										!!cartNumbers.find((cartNumber) => cartNumber.accountNumber === row.accountNum),
									Cell: ({ original }: { original: SingleNumber }) => {
										const checked = !!cartNumbers.find(
											(cartNumber) => cartNumber.accountNumber === original.accountNum
										);
										const text = checked ? translate('selected') : translate('select');
										return (
											<ConditionalRender
												show={!!original?.disabled}
												onTrue={() => (
													<LinkCell
														disabled={!!original?.disabled}
														path={'/dashboard/purchase-bulk-devices'}
														text={translate('ineligible')}
													/>
												)}
												onFalse={() => (
													<CheckboxCell
														text={text}
														checked={checked}
														onClick={() => {
															onNumberSelected(original, !checked);
														}}
														labelProps={{
															fontWeight: '600',
															color: primaryColor[0],
														}}
													/>
												)}
											/>
										);
									},
									minWidth: 150,
								},
							],
						}}
					/>
				</TableContainer>
			</div>
		</>
	);
};
export default SelectNumbersList;
